import * as React from 'react';
import { Button, Image, Icon } from '@rocketmakers/armstrong';

import { apiHooks } from '../api-clients/application';
import { useToastOnError } from '../hooks/useApiError';

import { Message, MessageTypes } from './message';
import { IUploaded } from '../api-clients/_api';
import './imageUpload.scss';

interface ImageUploadProps {
  existingImage?: string;
  imageName: string;
  onUpload: (uploaded: IUploaded) => void;
}

interface UploadState {
  file: File;
  preview: string;
  url: string;
  isLoading: boolean;
}

export const ImageUpload: React.FunctionComponent<ImageUploadProps> = ({ imageName, existingImage, onUpload }) => {
  const initialState: UploadState = { file: null, preview: null, url: null, isLoading: false };
  const [uploadImage, { data: uploadedFileName, isFetching: isUploading, error: errorUploading }] =
    apiHooks.storage.uploadFile.useMutation();

  const downloadFile = apiHooks.storage.downloadFile.useRequest();

  const [image, setImage] = React.useState(initialState);
  const imageUrl = image.url || image.preview;

  const shouldLoadExisting = existingImage && !imageUrl && !image.isLoading;
  const hasUploadError = !isUploading && errorUploading && !image.url;
  const hasUploaded = !isUploading && !!uploadedFileName && !image.isLoading;

  useToastOnError(errorUploading);

  const downloadImage = React.useCallback(
    async (fileName: string) => {
      setImage({ ...image, isLoading: true });

      const blob = await downloadFile({ fileName });

      setImage({ ...image, preview: null, url: URL.createObjectURL(blob), isLoading: false });
    },
    [image]
  );

  React.useEffect(() => {
    if (hasUploaded && !image.url) {
      downloadImage(uploadedFileName);
      onUpload(uploadedFileName);
    }
  }, [hasUploaded, uploadedFileName, onUpload, downloadImage]);

  React.useEffect(() => {
    if (shouldLoadExisting && existingImage) {
      downloadImage(existingImage);
    }
  }, [existingImage, shouldLoadExisting, downloadImage]);

  const handleUpdateImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    const file = e.target.files[0];

    setImage({ file, preview: URL.createObjectURL(file), url: null, isLoading: false });
    onUpload(null); // clear previous upload
  };

  const handleUpload = async () => {
    const [ext] = image.file.name.match(/\.[0-9a-z]+$/i);
    const uploadData = new FormData();
    uploadData.append('file', image.file, `${imageName}${ext}`);

    uploadImage(uploadData);
  };

  const handleCancel = () => {
    setImage(initialState);
  };

  return (
    <div className="image-upload">
      <Button className="image-capture-button" pending={image.isLoading}>
        <label className="image-capture">
          {!imageUrl ? 'Capture & Upload' : 'Change image'}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="image-input"
            onChange={handleUpdateImage}
            value={!image.file ? '' : undefined}
          />
        </label>
      </Button>

      {imageUrl && (
        <div className="image-preview">
          <Image src={imageUrl} renderSpinner={isUploading || image.isLoading} />

          {isUploading && <Message type={MessageTypes.Warning} title="Uploading image..." />}
          {hasUploaded && <Message type={MessageTypes.Success} title="Image uploaded" />}
          {hasUploadError && <Message type={MessageTypes.Error} title="Upload failed" />}

          {!image.url && (
            <>
              <h3 className="t-align-center">Are you happy with this photo?</h3>

              <div className="row align-con-h-center">
                <Button
                  data-qa="yes-image-button"
                  onClick={handleUpload}
                  title="Yes, upload."
                  className="bg-positive m-right-medium"
                >
                  <Icon icon={Icon.Icomoon.checkmarkCircle} />
                </Button>

                <Button
                  data-qa="no-image-button"
                  onClick={handleCancel}
                  title="No, I'll change it."
                  className="bg-negative"
                >
                  <Icon icon={Icon.Icomoon.cancelCircle2} />
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
